import React from 'react';
import { useRootPreviewMetadata } from 'js/file_viewer/api/data';
import { useUpdateArchiveFileCurrentPathCallback } from 'js/file_viewer/api/navigation';
import { useFileViewerContext } from 'js/file_viewer/contexts/file_viewer';
import { useSetLocalErrorCallback } from 'js/file_viewer/core/atoms/error';
import { concatenateArchivePath, currentArchivePathAtom } from 'js/file_viewer/core/atoms/metadata';
import { useFileViewerAtomValue } from 'js/file_viewer/core/atoms/use_atom';
import { isNestedArchiveFilePreviewKey } from 'js/file_viewer/core/data/preview_key';
import { archiveSubpathQueryParam } from 'js/file_viewer/core/middlewares/url';
import { useArchiveDataQuery } from 'js/file_viewer/core/queries/riviera_preview_types/archive';
import { ErrorLifecycle, FVError, FVErrorCode } from 'js/file_viewer/core/types/errors';
import { useCurrentPreviewKey, useRootPreviewKey } from 'js/file_viewer/hooks/selectors';
/**
 * Because the subpath can be extracted from the URL if a user navigates
 * directly to an archive preview, it's possible the current subpath is for
 * a folder that does not exist in the archive.  This function will return a
 * subpath that is the closest ancestor to the given path that actually is in
 * the archive tree structure.
 */
const getClosestSubpath = (entries, path) => {
    for (let i = path.length; i >= 0; i--) {
        const currentSubpath = path.slice(0, i);
        if (entries[concatenateArchivePath(currentSubpath)]) {
            return currentSubpath;
        }
    }
    return [];
};
/**
 * Walks an ArchiveEntry tree and returns the entry at a given subpath. If a node cannot be found
 * following the given path, it returns the deepest entry along the path.
 */
export function useArchiveEntryAtCurrentPath(entries) {
    const currentPath = useFileViewerAtomValue(currentArchivePathAtom);
    return React.useMemo(() => {
        const concatenatedPath = concatenateArchivePath(currentPath);
        return entries[concatenatedPath];
    }, [currentPath, entries]);
}
function queryStringValueToArchiveCurrentPath(rivieraData, queryStringValue) {
    var _a;
    if (!queryStringValue) {
        return [];
    }
    // We filter here because the path starts with `/` and so would otherwise
    // have a leading empty part.  Additionally, this will guard against users
    // manually entering paths that end with `/` or having multiple `/` in a row
    return getClosestSubpath(rivieraData.entries, (_a = queryStringValue === null || queryStringValue === void 0 ? void 0 : queryStringValue.split('/').slice(1).filter(Boolean)) !== null && _a !== void 0 ? _a : []);
}
export const getChildFilePaths = (entries, folderPath) => reduceChildren(entries, folderPath, (entry, path) => (!(entry === null || entry === void 0 ? void 0 : entry.isDir) ? path : undefined));
export const getChildren = (entries, folderPath) => reduceChildren(entries, folderPath, (entry) => entry);
/**
 * Returns an array of all the children of `folderPath` for which
 * the selector returns a non-undefined result
 */
const reduceChildren = (entries, folderPath, selector) => {
    var _a, _b, _c;
    return (_c = (_b = (_a = entries[folderPath]) === null || _a === void 0 ? void 0 : _a.children) === null || _b === void 0 ? void 0 : _b.reduce((result, childName) => {
        const childPath = `${folderPath}/${childName}`;
        const selectorResult = selector(entries[childPath], childPath);
        return selectorResult !== undefined ? [...result, selectorResult] : result;
    }, [])) !== null && _c !== void 0 ? _c : [];
};
export const useUrlControl = (rivieraData) => {
    const urlClient = useFileViewerContext().optionalIOClients.urlClient;
    const updateArchiveFileCurrentPathCallback = useUpdateArchiveFileCurrentPathCallback();
    const currentEntry = useArchiveEntryAtCurrentPath(rivieraData.entries);
    React.useEffect(() => {
        const updatePath = (path) => {
            var _a;
            const currentPath = queryStringValueToArchiveCurrentPath(rivieraData, path);
            return updateArchiveFileCurrentPathCallback(currentPath, (_a = currentEntry === null || currentEntry === void 0 ? void 0 : currentEntry.isDir) !== null && _a !== void 0 ? _a : false, 
            /* fromUrlChanged */ true);
        };
        return urlClient === null || urlClient === void 0 ? void 0 : urlClient.onQueryStringParameterChange(archiveSubpathQueryParam, (_, value) => updatePath(value));
    }, [updateArchiveFileCurrentPathCallback, urlClient, currentEntry === null || currentEntry === void 0 ? void 0 : currentEntry.isDir, rivieraData]);
};
export const useMaybeSetPasswordProtectedArchiveError = () => {
    var _a, _b;
    const rootPreviewMetadata = useRootPreviewMetadata();
    const rootPreviewKey = useRootPreviewKey();
    const archiveData = useArchiveDataQuery(rootPreviewKey, rootPreviewMetadata);
    const currentPreviewKey = useCurrentPreviewKey();
    const nestedArchivePreviewKey = currentPreviewKey && isNestedArchiveFilePreviewKey(currentPreviewKey)
        ? currentPreviewKey
        : undefined;
    const setLocalError = useSetLocalErrorCallback(nestedArchivePreviewKey);
    const isPasswordProtected = ((_b = (_a = archiveData.data) === null || _a === void 0 ? void 0 : _a.apiData) === null || _b === void 0 ? void 0 : _b.isPasswordProtected) && !!nestedArchivePreviewKey;
    React.useEffect(() => {
        if (isPasswordProtected) {
            setLocalError(new FVError(FVErrorCode.PasswordProtectedArchivesNotSupported, ErrorLifecycle.Support));
        }
    }, [isPasswordProtected, setLocalError]);
};
export const useSyncUrlParamWithState = (rivieraData) => {
    const { optionalIOClients: { urlClient }, } = useFileViewerContext();
    const setArchivePath = useUpdateArchiveFileCurrentPathCallback();
    React.useEffect(() => {
        var _a, _b;
        if (!(urlClient === null || urlClient === void 0 ? void 0 : urlClient.getQueryStringParameter(archiveSubpathQueryParam))) {
            return;
        }
        const urlSubpath = queryStringValueToArchiveCurrentPath(rivieraData, urlClient.getQueryStringParameter(archiveSubpathQueryParam));
        setArchivePath(urlSubpath, (_b = (_a = rivieraData.entries[concatenateArchivePath(urlSubpath)]) === null || _a === void 0 ? void 0 : _a.isDir) !== null && _b !== void 0 ? _b : false, true);
    }, [rivieraData, setArchivePath, urlClient]);
};
//# sourceMappingURL=utils.preenvesnextjs.map
